import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SectionHeading from '../home/SectionHeading';
import { useT } from '../../i18n/LanguageContext';

// The AI architecture behind the tool, framed as capabilities — competent,
// not a spec dump. Auto-scrolling horizontal marquee of equal-height cards.
// Visuals are the Fluel brand card icons (deliverable/ICONS/SVG), a
// different set from the animated method-icons in PlatformDeliverables.
const STACK_ICONS = ['sezione1', 'sezione6', 'sezione11', 'sezione16', 'sezione21', 'sezione26', 'sezione31'].map((icon) => {
  // CRA's SVG loader returns a module object ({ default: url, ReactComponent });
  // image loaders return the URL string directly. Handle both.
  const mod = require(`../../assets/img/ai-stack/${icon}.svg`);
  return { icon, iconSrc: (mod && mod.default) || mod };
});

function getStack(t) {
  return STACK_ICONS.map(({ icon, iconSrc }, i) => ({
    icon, iconSrc,
    title: t(`platform.aiStack.item${i + 1}Title`),
    text: t(`platform.aiStack.item${i + 1}Text`),
    tags: t(`platform.aiStack.item${i + 1}Tags`),
  }));
}

function StackCard({ item, accent }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flexShrink: 0, width: { xs: 270, md: 330 },
        minHeight: { xs: 440, md: 430 }, scrollSnapAlign: 'start',
        display: 'flex', flexDirection: 'column', gap: 1.5,
        p: { xs: 2.5, md: 3 }, borderRadius: 4, bgcolor: '#fff',
        border: `1px solid ${theme.home.paperDeep}`,
        boxShadow: theme.home.softShadow || '0 6px 18px -10px rgba(20,24,29,0.28)',
        whiteSpace: 'normal',
      }}
    >
      <Box
        sx={{
          borderRadius: 3, p: 2.5, display: 'grid', placeItems: 'center',
          background: `linear-gradient(135deg, ${theme.home.cyan}14, ${theme.home.coral}14)`,
          border: `1px solid ${theme.home.paperDeep}`,
        }}
      >
        <Box component="img" src={item.iconSrc} alt="" loading="lazy"
          sx={{ width: { xs: 80, md: 92 }, height: { xs: 80, md: 92 }, display: 'block' }} />
      </Box>
      <Typography
        variant="h3"
        sx={{
          fontFamily: theme.home.display, fontWeight: 700,
          fontSize: { xs: '1.15rem', md: '1.3rem' }, color: theme.home.ink,
        }}
      >
        {item.title}
      </Typography>
      <Typography sx={{ color: theme.home.inkMuted, lineHeight: 1.6, fontSize: '0.92rem', flexGrow: 1 }}>
        {item.text}
      </Typography>
      <Typography sx={{ color: accent, fontSize: '0.78rem', fontWeight: 600 }}>
        {item.tags}
      </Typography>
    </Box>
  );
}

export default function PlatformAIStack() {
  const theme = useTheme();
  const t = useT();
  const STACK = getStack(t);
  // Two copies back-to-back so translateX(-50%) loops seamlessly.
  const loop = [...STACK, ...STACK];

  return (
    <Box sx={{ bgcolor: theme.home.paper, py: theme.home.sectionPy, overflow: 'hidden' }}>
      <Box sx={{ maxWidth: theme.home.maxWidth, mx: 'auto', px: { xs: 3, md: 5 }, mb: { xs: 4, md: 6 } }}>
        <SectionHeading
          eyebrow={t('platform.aiStack.eyebrow')}
          title={t('platform.aiStack.title')}
          lede={t('platform.aiStack.lede')}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
          maskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
          '&:hover .aistack-track, &:focus-within .aistack-track': { animationPlayState: 'paused' },
        }}
      >
        <Box
          className="aistack-track"
          sx={{
            display: 'flex', alignItems: 'stretch', gap: { xs: 2.5, md: 3 },
            width: 'max-content', px: { xs: 3, md: 5 },
            animation: 'aistack-scroll 60s linear infinite',
            '@keyframes aistack-scroll': {
              from: { transform: 'translateX(0)' },
              to: { transform: 'translateX(-50%)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              width: '100%',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
            },
          }}
        >
          {loop.map((item, i) => (
            <StackCard key={`${item.title}-${i}`} item={item}
              accent={i % 2 === 0 ? theme.home.cyanText : theme.home.coralText} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
